import {
  batchCreateAge,
  batchCreateBasicField,
  batchCreateName,
  batchCreateNameSuper,
  batchDeleteElementAndSubs,
  batchMoveSurnameShareToNameSurn,
  batchRemoveOrUnSubSurname,
  batchRemoveOrUnSubSurnameExtension,
  createFieldValue,
  fieldToElementTypeMap,
  getImage,
  getNestedOrFloatingElementById,
  getPersonIdsForField,
  INDEXED_INCORRECTLY,
  isSharedField,
  sexTypes,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'
import { getNextUuid } from '../../../../../../shared/utils/utils'
import { submitEditEvents, getFieldIdAndValue, superDeduper } from './editPanelDispatchBuilders'

/**
 * Filters dispatches to only include those related to a specific highlight field
 * @param {Array} dispatches - The array of dispatches to filter
 * @param {Object} highlightsFieldInfo - Information about the highlight field
 * @param {Object} addSubDispatch - The ADD_SUB dispatch for the field
 * @returns {Array} Filtered array of dispatches
 */
const filterDispatchesForHighlightField = (dispatches, highlightsFieldInfo, addSubDispatch) => {
  return dispatches.filter((dispatch) => {
    return (
      dispatch.value.id === highlightsFieldInfo.id ||
      dispatch === addSubDispatch ||
      (addSubDispatch && dispatch.value.id === addSubDispatch.value.superId) ||
      (addSubDispatch && dispatch.value.subId === addSubDispatch.value.superId)
    )
  })
}

export const getCreateDispatchesForSingleEventField = ({
  values,
  images,
  selectedPersonId,
  highlightsFieldInfo,
  formState,
  types,
  addedEventId,
  eventTypeFieldId,
  currentImageArk,
}) => {
  const dispatches = submitEditEvents({
    values,
    images,
    selectedPersonId,
    formState,
    types,
    addedEventId,
    eventTypeFieldId,
    currentImageArk,
  })
  const addSubDispatch = dispatches.find((dispatch) => dispatch.value.subId === highlightsFieldInfo.id)
  return filterDispatchesForHighlightField(dispatches, highlightsFieldInfo, addSubDispatch)
}

export const handleDeleteField = (el, key, hasSecondarySurnameEl, selectedPerson, images) => {
  let dispatches = []
  if (el.fieldType === 'NAME_SURN') {
    const nameSurnameElement = getNestedOrFloatingElementById(
      [selectedPerson],
      el.superElements.find((superEl) => getNestedOrFloatingElementById([selectedPerson], superEl.id))?.id
    )
    if (hasSecondarySurnameEl) {
      // Only remove the NAME_SURN field
      if (el.superElements.length > 1) {
        dispatches.push({
          type: 'REM_SUB',
          value: {
            superPartitionKey: nameSurnameElement.partitionKey,
            subPartitionKey: el.partitionKey,
            superId: nameSurnameElement.id,
            subId: key,
            elementType: el.elementType,
          },
        })
      } else {
        dispatches.push({
          type: 'REMOVE_ELEMENT',
          value: {
            id: key,
            partitionKey: el.partitionKey,
            elementType: el.elementType,
          },
        })
      }
    } else {
      dispatches = superDeduper(
        dispatches.concat(batchRemoveOrUnSubSurname({ nameSurnameElement, person: selectedPerson, images }))
      )
    }
  } else if (el.fieldType === 'NAME_SPRE' || el.fieldType === 'NAME_PATR' || el.fieldType === 'NAME_MATR') {
    dispatches = superDeduper(
      dispatches.concat(batchRemoveOrUnSubSurnameExtension({ extensionField: el, person: selectedPerson, images }))
    )
  } else {
    dispatches = superDeduper(dispatches.concat(batchDeleteElementAndSubs({ element: el })))
  }
  return dispatches
}

/**
 * Get data about surname elements that takes into account being deleted and added.
 * @param formValues - methods.getValues of values from submit
 * @param selectedPerson - edited person object
 * @returns {{surnamePartitionKey: string, surnameElId: string, hasSecondarySurnameEl: boolean}}
 */
export const gatherSurnameInformation = (formValues, selectedPerson) => {
  let hasSecondarySurnameEl = false
  let surnameElId = ''
  let surnamePartitionKey = ''

  Object.entries(formValues).forEach(([key, value]) => {
    if (value === 'NAME_SPRE' || value === 'NAME_PATR' || value === 'NAME_MATR') {
      hasSecondarySurnameEl = true
    }
    if (value === 'NAME_SURN') {
      surnameElId = getNextUuid()
      surnamePartitionKey = selectedPerson.partitionKey
    }
    const el = getNestedOrFloatingElementById([selectedPerson], key)
    if (el) {
      if (
        value !== 'REMOVED' &&
        (el.fieldType === 'NAME_SPRE' || el.fieldType === 'NAME_PATR' || el.fieldType === 'NAME_MATR')
      ) {
        hasSecondarySurnameEl = true
      } else if (el.fieldType === 'NAME_SURN') {
        el.superElements.forEach((superEl) => {
          if (getNestedOrFloatingElementById([selectedPerson], superEl.id)) {
            surnameElId = superEl.id
            surnamePartitionKey = superEl.partitionKey
          }
        })
      }
    }
  })

  return { hasSecondarySurnameEl, surnameElId, surnamePartitionKey }
}

/**
 * Extracts element IDs from a dispatch object based on its type.
 * Note: Returns element IDs (field, person, or super element IDs) that may be relevant for
 * determining which records are affected by the dispatch.
 * @param {Object} dispatch - The dispatch object to extract element IDs from
 * @returns {Array<string>} Array of element IDs (may include super element and sub element IDs)
 */
const getElementIdsFromDispatch = (dispatch) => {
  if (!dispatch?.type || !dispatch?.value) return []

  switch (dispatch.type) {
    case 'ADD_FV':
    case 'SET_NAME_TYPE':
    case 'CREATE_ELEMENT':
    case 'REMOVE_ELEMENT':
    case 'SET_PRIMARY':
      return [dispatch.value.id]
    case 'ADD_SUB':
    case 'REM_SUB':
      // For ADD_SUB/REM_SUB, both the super element and sub element may be relevant
      // If the super element is shared, all records with persons sharing it are affected
      return [dispatch.value.superId, dispatch.value.subId].filter(Boolean)
    default:
      return []
  }
}

/**
 * Identifies which record IDs need SET_RECORD_PERSON_IDS dispatches.
 * For shared elements, all records containing persons who share the element are affected.
 * For non-shared elements, only the current record is affected.
 * Note: For ADD_SUB/REM_SUB dispatches, checks both the super element and sub element for sharing,
 * ensuring that mutations to shared super elements correctly update all affected records.
 * @param {Object} params - Parameters object
 * @param {Array} params.dispatches - The dispatches generated so far
 * @param {Array} params.images - All images in the current context
 * @param {Object} params.selectedPerson - The person being edited
 * @returns {Set<string>} Set of record IDs that need to be updated
 */
const getAffectedRecordIds = ({ dispatches, images, selectedPerson }) => {
  const recordIds = new Set()

  // Always include the current record as a fallback
  if (selectedPerson.recordId) {
    recordIds.add(selectedPerson.recordId)
  }

  // Extract element IDs from all dispatches
  const elementIds = new Set()
  dispatches.forEach((dispatch) => {
    const ids = getElementIdsFromDispatch(dispatch)
    ids.forEach((id) => elementIds.add(id))
  })

  // For each element, determine affected records
  elementIds.forEach((elementId) => {
    const element = getNestedOrFloatingElementById(images, elementId)
    if (!element) {
      return // Element might not exist yet (being created)
    }

    const isShared = isSharedField(elementId, images)

    if (isShared) {
      // Shared element: affects multiple persons across potentially multiple records
      const personIds = getPersonIdsForField(element, images)
      personIds.forEach((personId) => {
        const person = getNestedOrFloatingElementById(images, personId)
        if (person?.recordId) {
          recordIds.add(person.recordId)
        }
      })
      return
    }

    // Non-shared element: only affects the current person/record
    if (selectedPerson.recordId) {
      recordIds.add(selectedPerson.recordId)
    }
  })

  return recordIds
}

export const submitEditFields = ({ values, images, selectedPersonId, inPanelStack, formState, currentImageArk }) => {
  let dispatches = []
  const currentImage = getImage(images, currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], selectedPersonId)
  let createdAgeId = ''
  const { hasSecondarySurnameEl, surnameElId, surnamePartitionKey } = gatherSurnameInformation(values, selectedPerson)

  Object.entries(values).forEach(([key, value]) => {
    const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: R2AppName }
    const el = getNestedOrFloatingElementById([selectedPerson], key)
    // the field already exists - either remove it or update its value
    if (el) {
      if (value === 'REMOVED' || value?.key === 'REMOVED') {
        dispatches = dispatches.concat(handleDeleteField(el, key, hasSecondarySurnameEl, selectedPerson, images))
      } else if (formState.dirtyFields[key]) {
        if (el.elementType === 'NAME') {
          dispatches.push({
            type: 'SET_NAME_TYPE',
            value: {
              id: key,
              partitionKey: selectedPerson.partitionKey,
              nameType: value,
            },
          })
        } else {
          const { id, value: fieldValue } = getFieldIdAndValue({ el, value, attribution })
          dispatches.push({ type: 'ADD_FV', value: { partitionKey: el.partitionKey, id, fieldValue } })
        }
      }
    } else if (key === 'primaryRadio') {
      if (formState.dirtyFields[key]) {
        dispatches.push({
          type: 'SET_PRIMARY',
          value: {
            id: selectedPersonId,
            partitionKey: selectedPerson.partitionKey,
            primary: value === 'yes',
          },
        })
      }
    } else if (
      !key.includes('fieldType') &&
      !key.includes('nameField') &&
      !key.includes('nameSuper') &&
      !key.includes('newNameElement') &&
      !Object.keys(values).includes(`${key}_nameField`) &&
      !Object.keys(values).includes(`${key}_newNameElement`)
    ) {
      if (value !== 'REMOVED') {
        // create new age field and possibly intermediary AGE element as well
        // we use the form to hold metadata about this field -
        // (e.g. {'<id>': '<value>', '<id>_fieldType': 'AGE'})
        let fieldValue = createFieldValue(value, attribution)

        const isSexField = sexTypes.includes(value?.key)
        if (isSexField) {
          fieldValue = createFieldValue(value.primaryText, attribution, `http://gedcomx.org/${value.key}`)
        }
        const superId = inPanelStack('editAge')
          ? selectedPerson.subElements.find((se) => se.elementType === 'AGE')?.id || createdAgeId
          : selectedPerson.id
        const superElementType = inPanelStack('editAge') ? 'AGE' : 'PERSON'
        if (!superId && superElementType === 'AGE') {
          const batchCreateAgeParams = {
            partitionKey: selectedPerson.partitionKey,
            personId: selectedPerson.id,
            id: key,
            fieldType: values[`${key}_fieldType`],
            fieldValue,
          }
          dispatches = dispatches.concat(batchCreateAge(batchCreateAgeParams))
          const newAgeElement = dispatches.find((d) => d.type === 'CREATE_ELEMENT' && d.value.elementType === 'AGE')
          createdAgeId = newAgeElement.value.id
        } else {
          const valueExists =
            (typeof values[key] === 'string' && values) || (typeof values[key] === 'object' && values[key].key)
          if (valueExists) {
            // create any other type of field
            const batchCreateAdditionalFieldParams = {
              partitionKey: selectedPerson.partitionKey,
              superId,
              id: key,
              fieldType: values[`${key}_fieldType`],
              fieldValue,
              superElementType,
            }
            dispatches = dispatches.concat(batchCreateBasicField(batchCreateAdditionalFieldParams))
          }
        }
      }
    } else if (
      !key.includes('nameField') &&
      !key.includes('nameSuper') &&
      Object.keys(values).includes(`${key}_nameField`) &&
      value !== 'REMOVED'
    ) {
      // create structured name subElement (e.g. NAME_SURNAME + NAME_SURN)
      // we use the form to hold metadata about this name element -
      // (e.g. {'<id>': '<value>', '<id>_nameField': 'NAME_GN', '<id>_nameSuper': '<nameSuperId>'})
      const fieldType = values[`${key}_nameField`] // e.g. NAME_SURNAME
      const elementType = fieldToElementTypeMap[fieldType]
      const nameSuperId = values[`${key}_nameSuper`]
      const fieldValue = createFieldValue(value, attribution)
      if (fieldType === 'NAME_SPRE' || fieldType === 'NAME_PATR' || fieldType === 'NAME_MATR') {
        if (!surnameElId) {
          dispatches = dispatches.concat(
            batchCreateName({
              partitionKey: selectedPerson.partitionKey,
              nameSuperId,
              fieldValue,
              fieldType,
              elementType,
              nameFieldId: key,
            })
          )
        } else {
          if (isSharedField(surnameElId, [selectedPerson])) {
            // Switch share from NAME_SURNAME to NAME_SURN level. All fields will have new NAME_SURNAMES created that point to the same NAME_SURN
            const surnameEl = getNestedOrFloatingElementById([selectedPerson], surnameElId)
            dispatches = dispatches.concat(batchMoveSurnameShareToNameSurn(surnameEl))
          }
          const selectedPersonName = selectedPerson.subElements.find((se) => se.elementType === 'NAME')
          const newSurnameId = dispatches.find(
            (disp) => disp.type === 'ADD_SUB' && disp.value.superId === selectedPersonName.id
          )?.value.subId
          dispatches = dispatches.concat(
            batchCreateBasicField({
              partitionKey: surnamePartitionKey,
              superId: newSurnameId || surnameElId,
              fieldType,
              fieldValue,
              superElementType: 'NAME_SURNAME',
            })
          )
        }
      } else {
        dispatches = dispatches.concat(
          batchCreateName({
            partitionKey: selectedPerson.partitionKey,
            nameSuperId,
            fieldValue,
            fieldType,
            elementType,
            nameFieldId: key,
            nameElementId: elementType === 'NAME_SURNAME' ? surnameElId : getNextUuid(),
          })
        )
      }
    } else if (!key.includes('newNameElement') && Object.keys(values).includes(`${key}_newNameElement`)) {
      // create new NAME element
      dispatches = dispatches.concat(
        batchCreateNameSuper({
          partitionKey: selectedPerson.partitionKey,
          nameElementId: key,
          nameType: value,
          personId: selectedPerson.id,
        })
      )
    }
  })
  // Update only affected records (not all records)
  // For shared fields, this updates all records containing persons who share the field
  // For non-shared fields, this only updates the current record
  const affectedRecordIds = getAffectedRecordIds({ dispatches, images, selectedPerson })

  affectedRecordIds.forEach((recordId) => {
    dispatches.push({
      type: 'SET_RECORD_PERSON_IDS',
      value: recordId,
    })
  })

  return dispatches
}

export const getCreateDispatchesForSingleField = ({
  values,
  images,
  selectedPersonId,
  highlightsFieldInfo,
  inPanelStack,
  formState,
  currentImageArk,
}) => {
  const dispatches = submitEditFields({
    values,
    images,
    selectedPersonId,
    inPanelStack,
    formState,
    currentImageArk,
  })
  const addSubDispatch = dispatches.find((dispatch) => dispatch.value.subId === highlightsFieldInfo.id)
  return filterDispatchesForHighlightField(dispatches, highlightsFieldInfo, addSubDispatch)
}

export function handleFieldChange(dispatch) {
  dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: '' })
}

export function handleOnFocus({ element, r2Dispatch }) {
  const activeFieldId = element?.subElements ? element?.subElements?.[0]?.id : element.id
  r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: activeFieldId })
}

/**
 * Remove a field and all associated information from form values
 * Leaves the form in a clean state if you remove a field that wasn't in the form originally
 * @param field - field to remove
 * @param methods - ZionForm methods object
 */
export function removeFieldFromValues(field, methods) {
  if (typeof methods.formState.defaultValues[field.id] !== 'undefined') {
    methods.setValue(field.id, 'REMOVED')
  } else {
    const newValues = methods.getValues()
    Object.keys(newValues).forEach((key) => {
      if (key.includes(field.id)) delete newValues[key]
    })
    methods.reset(newValues)
  }
}
